import { Trans } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { useActionName } from '@/models/actionType';
import { type ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { ActionAdditionalInfoInputs, ActionAdditionalInfoMobile } from './ActionAdditionalInfo';
import {
  ActionBrandResponsibilityInput,
  ActionBrandResponsibilityMobile,
} from './ActionBrandResponsibility';
import { ActionCardHeader } from './ActionCardHeader';
import { ActionContextInput, ActionContextMobile } from './ActionContext';
import { ActionDefectPhotosInput, ActionDefectPhotosMobile } from './ActionDefectPhotos';
import { ActionDefectsInput, ActionDefectsMobile } from './ActionDefects';
import { ActionPrices } from './ActionPrices';
import { ActionSpareParts } from './ActionSpareParts';
import { ActionSparePartsMobileBadges } from './ActionSparePartsMobile';

import './ActionCard.css';

const { element } = createBEMClasses('actions-list');

export const ActionCard = ({
  action,
  previousAction,
  duplicateActionNumber,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  duplicateActionNumber?: number;
}) => {
  const { isMobile } = useViewPort();
  const { article, errors } = useArticleContext();

  const actionOrPreviousAction = action ?? previousAction!;

  const actionName = useActionName({ action: actionOrPreviousAction });

  if (!action && !previousAction) {
    return null;
  }

  const hasError = !!errors.services?.actions?.actions?.find((act) => act.id === action?.id)
    ?.hasError;

  const isDeletedAction = !action;

  return (
    <Box
      padding="0"
      ariaLabel={duplicateActionNumber ? `${actionName} #${duplicateActionNumber}` : actionName}
      style={
        hasError && isMobile
          ? {
              border: '1px solid var(--color-danger-700)',
            }
          : undefined
      }
      className={element('card', {
        new: !!action && !!article.previousSnapshot?.articleActions.length && !previousAction,
        deleted: !action,
      })}
    >
      {!action && (
        <span className="visually-hidden">
          <Trans id="article.form.actions.deleted-action">Deleted action</Trans>
        </span>
      )}

      <ActionCardHeader
        action={action}
        previousAction={previousAction}
        duplicateActionNumber={duplicateActionNumber}
      />

      <hr />

      {!isDeletedAction &&
        (isMobile ? (
          <ActionCardMobile action={action} previousAction={previousAction} />
        ) : (
          <>
            <ActionCardDesktop action={action} previousAction={previousAction} />
            <hr />
          </>
        ))}

      <ActionPrices action={action} previousAction={previousAction} />
    </Box>
  );
};

const ActionCardMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view } = useArticleContext();

  if (!action && !previousAction) {
    return null;
  }

  const showSpareParts = view.services.actions.fields.spareParts.shown;

  return (
    <Stack padding="12px" gap="0.75rem">
      <ActionAdditionalInfoMobile action={action} previousAction={previousAction} />
      {view.services.actions.fields.context.shown && (
        <ActionContextMobile action={action} previousAction={previousAction} />
      )}
      {view.services.actions.fields.photo.shown && (
        <ActionDefectPhotosMobile action={action} previousAction={previousAction} />
      )}
      <Stack gap="0.5rem" row>
        {view.services.actions.fields.associatedDefects.shown && (
          <ActionDefectsMobile action={action} previousAction={previousAction} />
        )}
        {view.services.actions.fields.brandResponsibility.shown && (
          <ActionBrandResponsibilityMobile action={action} previousAction={previousAction} />
        )}
        {showSpareParts && (
          <ActionSparePartsMobileBadges action={action} previousAction={previousAction} />
        )}
      </Stack>
    </Stack>
  );
};

const ActionCardDesktop = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { isTablet } = useViewPort();
  const { view } = useArticleContext();

  if (!action && !previousAction) {
    return null;
  }

  const showContextOrPhotos =
    view.services.actions.fields.context.shown || view.services.actions.fields.photo.shown;
  const showContextAndPhotos =
    view.services.actions.fields.context.shown && view.services.actions.fields.photo.shown;

  const showSpareParts = view.services.actions.fields.spareParts.shown;

  return (
    <Stack padding="12px" gap="1rem">
      <ActionAdditionalInfoInputs action={action} previousAction={previousAction} />
      {showContextOrPhotos && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isTablet || !showContextAndPhotos ? '1fr' : '1fr 1fr',
            gap: '1rem',
          }}
        >
          {view.services.actions.fields.photo.shown && (
            <ActionDefectPhotosInput action={action} previousAction={previousAction} />
          )}
          {view.services.actions.fields.context.shown && (
            <ActionContextInput action={action} previousAction={previousAction} />
          )}
        </div>
      )}
      {view.services.actions.fields.associatedDefects.shown && (
        <ActionDefectsInput action={action} previousAction={previousAction} />
      )}
      {view.services.actions.fields.brandResponsibility.shown && (
        <ActionBrandResponsibilityInput action={action} previousAction={previousAction} />
      )}
      {showSpareParts && <ActionSpareParts action={action} previousAction={previousAction} />}
    </Stack>
  );
};
